import { useQuery } from '@tanstack/react-query'
import React, { ReactElement, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Gmail2026 from 'src/assets/svgs/Gmail2026.svg'
import GoogleEmailIntegrationModal from 'src/component-modal/GoogleEmailIntegrationModal/GoogleEmailIntegrationModal'
import GoogleWorkspaceIntegrationManageModal from 'src/component-modal/GoogleWorkspaceIntegrationManageModal/GoogleWorkspaceIntegrationManageModal'
import { useTranslate } from 'src/localization/Trns'
import {
  emailIntegrationQueries,
  useDisconnectGoogleEmailIntegration,
} from 'src/queries/organizations/emailIntegrations/emailIntegrations.queries'

import GoogleWorkspaceIntegrationCard from './GoogleWorkspaceIntegrationCard'

type GoogleEmailIntegrationCardProps = {
  organizationId: string
  canManage: boolean
  locale?: string
}

export default function GoogleEmailIntegrationCard({
  organizationId,
  canManage,
  locale = 'en-US',
}: GoogleEmailIntegrationCardProps): ReactElement {
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const [showSetup, setShowSetup] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const integrationQuery = useQuery(
    emailIntegrationQueries.google(organizationId),
  )
  const disconnect = useDisconnectGoogleEmailIntegration({
    onSuccess: () => {
      setShowManage(false)
      analytics.track('email integration', {
        action: 'disconnect_succeeded',
        provider: 'GOOGLE',
      })
    },
  })

  const integration = integrationQuery.data
  const openSetup = () => {
    setShowSetup(true)
    analytics.track('email integration', {
      action: 'setup_started',
      provider: 'GOOGLE',
      status: integration?.status,
    })
  }

  const openManage = () => {
    disconnect.reset()
    setShowManage(true)
    analytics.track('email integration', {
      action: 'manage_opened',
      provider: 'GOOGLE',
    })
  }

  return (
    <>
      <GoogleWorkspaceIntegrationCard
        name={translate('Gmail')}
        description={translate(
          'Automatically capture email receipts and match them to the right transactions.',
        )}
        icon={<img src={Gmail2026} alt="" />}
        status={integration?.status}
        canManage={canManage}
        onSetup={openSetup}
        onManage={openManage}
        loading={integrationQuery.isLoading}
        error={integrationQuery.isError || !integration}
        onRetry={() => integrationQuery.refetch()}
      />
      {integration && (
        <>
          <GoogleEmailIntegrationModal
            isOpen={showSetup}
            onClose={() => setShowSetup(false)}
            organizationId={organizationId}
            integration={integration}
          />
          <GoogleWorkspaceIntegrationManageModal
            isOpen={showManage}
            onClose={() => setShowManage(false)}
            integrationName={translate('Gmail')}
            workspaceDomain={integration.workspaceDomain}
            connectedAt={integration.connectedAt}
            lastVerifiedAt={integration.lastVerifiedAt}
            locale={locale}
            onVerifyAgain={() => {
              setShowManage(false)
              openSetup()
            }}
            onDisconnect={() => disconnect.mutate({ orgId: organizationId })}
            disconnectPending={disconnect.isPending}
            disconnectError={disconnect.isError}
          />
        </>
      )}
    </>
  )
}
